import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import { cloneDeep } from "lodash"

import Image from "../utils/Image"
import Reveal from "../utils/Reveal"

import Card from "../atoms/Card"
import Section from "../atoms/Section"
import FlexGrid from "../atoms/FlexGrid"
import Button from "../atoms/Button"
import Flex from "../atoms/Flex"
import Box from "../atoms/Box"

import InfoCard from "../molecules/InfoCard"
import SectionTitle from "../molecules/SectionTitle"
import FilteredGrid from "../molecules/FilteredGrid"
import GenericCarousel from "../molecules/GenericCarousel"

const InfoCards = ({
  section,
  sectionTitle,
  flexGrid,
  InfoCFlexGrid,
  infoCards,
  button,
  card,
  infoCardsTitleSize,
  infoCardsTitleColor,
  cardsTheme = "default",
  cardsBackground = 0,
  cardsShadow = "md",
  cardsAlign = "left",
  iconLocation = "top",
  image,
  infoCardsImageLocation,
  imageLocation,
  iconSize,
  filter,
  InfoCFilter,
  InfoCFilters,
  filters,
  useCarouselFront,
  carousel,
}) => {
  flexGrid = InfoCFlexGrid || flexGrid || { columns: { _: 1 } }
  filter = InfoCFilter || filter
  filters = InfoCFilters || filters || []

  let flexGridProps = cloneDeep(flexGrid),
    flexProps = {}

  const hasImage = !!image
  if (hasImage) {
    flexGridProps.columns = {
      _: 1,
    }
    flexProps.flexDirection = {
      _: "column-reverse",
    }
    flexGridProps.gutter = hasImage ? { _: 3, md: 4 } : flexGridProps.gutter
    if (imageLocation === "left" || infoCardsImageLocation === "left")
      flexProps.flexDirection.md = "row"
    else flexProps.flexDirection.md = "row-reverse"
  }

  /**
   * Logic for applying universal heights to cards for carousel layouts
   */
  const [cardMaxHeight, setCardMaxHeight] = useState(0)
  const cardRefs = useRef([])

  // Keep refs stable (do NOT recreate the array each render)
  if (cardRefs.current.length !== infoCards.length) {
    cardRefs.current = Array(infoCards.length).fill(null)
  }

  useEffect(() => {
    if (typeof window === "undefined" || !useCarouselFront) return

    let raf1 = null
    let raf2 = null
    let settleInterval = null

    const getMaxHeight = () => {
      // Prefer scrollHeight: it reflects natural content height even if height is constrained
      const heights = cardRefs.current.map(el => {
        if (!el) return 0
        const sh = el.scrollHeight || 0
        const oh = el.offsetHeight || 0
        return Math.max(sh, oh)
      })
      return Math.max(...heights, 0)
    }

    const measureAndSet = () => {
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => {
          const max = getMaxHeight()
          if (max > 0) setCardMaxHeight(prev => (max > prev ? max : prev))
        })
      })
    }

    // Reset first so we don’t keep an old value when presets inject new cards
    setCardMaxHeight(0)

    // Kick an initial measurement pass
    measureAndSet()

    // Fonts can change text metrics after first paint
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => measureAndSet())
    }

    // ResizeObserver for real-time adjustments (carousel/layout changes)
    const observer = new ResizeObserver(() => {
      measureAndSet()
    })

    cardRefs.current.forEach(el => {
      if (el) observer.observe(el)
    })

    // “Settle loop”: for the preset injection case, content can grow over a few frames.
    // Poll briefly and keep the max as it increases.
    let last = 0
    settleInterval = setInterval(() => {
      const max = getMaxHeight()
      if (max > last) {
        last = max
        setCardMaxHeight(prev => (max > prev ? max : prev))
      }
    }, 100)

    // Stop settle loop after ~1.2s (keeps it minimal + avoids permanent polling)
    const stopTimeout = setTimeout(() => {
      clearInterval(settleInterval)
    }, 1200)

    return () => {
      observer.disconnect()
      if (raf1) cancelAnimationFrame(raf1)
      if (raf2) cancelAnimationFrame(raf2)
      clearInterval(settleInterval)
      clearTimeout(stopTimeout)
    }
  }, [useCarouselFront, infoCards, infoCards.length, carousel])

  if (useCarouselFront) {
    const throughProps = { section, sectionTitle, carousel }

    return (
      <GenericCarousel {...throughProps}>
        {infoCards?.map((infoCard, i) => (
          <InfoCard
            key={i}
            ref={el => {
              cardRefs.current[i] = el
            }}
            iconLocation={iconLocation}
            iconSize={iconSize}
            align={cardsAlign}
            height={cardMaxHeight ? `${cardMaxHeight}px` : "min-content"}
            width={"300px"}
            {...infoCard}
            infoCardsTitleSize={infoCardsTitleSize}
            infoCardsTitleColor={infoCardsTitleColor}
            card={
              card || {
                useCard: true,
                bg: cardsBackground,
                borderRadius: "md",
                boxShadow: cardsShadow,
                theme: cardsTheme,
              }
            }
            ml={carousel?.spacing > 0 ? `${carousel.spacing / 2 + 8}px` : 0}
            mr={carousel?.spacing > 0 ? `${carousel.spacing / 2 - 8}px` : 0}
          />
        ))}
      </GenericCarousel>
    )
  }
  /**
   * End carousel layout logic and rendering - below renders grid layout
   */

  return (
    <Section {...section}>
      {sectionTitle && (
        <Reveal>
          <SectionTitle {...sectionTitle} mb={6} maxWidth={42} />
        </Reveal>
      )}
      <Flex
        width="100%"
        {...flexProps}
        justifyContent="space-between"
        style={{ gap: "32px" }} //space between the image column and the grid column
      >
        {hasImage && (
          <Box flex="0 0 48%" mt={{ _: 3, md: 0 }}>
            <Reveal>
              <Image
                alternativeText="Image In An Infocard"
                imageFieldId={`${section.sectionId}.image`}
                {...image}
              />
            </Reveal>
          </Box>
        )}

        <FilteredGrid
          flexGrid={{
            flex: hasImage ? "0 0 48%" : "1 1 auto",
            ...flexGridProps,
          }}
          filters={filters}
          filtersStyles={{
            filterLocation: filter?.filterLocation,
            alignMenuItems: filter?.alignMenuItems,
            showFilterLabel: filter?.showFilterLabel,
            layout: filter?.layout,
            filterTextDisplay: filter?.filterTextDisplay,
            dropdownCard: filter?.dropdownCard,
          }} // only sending style fields to avoid possibly overwriting filter data
          card={{
            theme: section.theme,
            bg: section.bg,
            borderRadius: "md",
            // boxShadow: "md",
            boxShadow: "0 3px 10px rgb(0 0 0 / 30%)",
          }}
        >
          {infoCards?.map((infoCard, i) => (
            <InfoCard
              key={i}
              mx={cardsAlign === "center" ? "auto" : 0}
              iconLocation={iconLocation}
              iconSize={iconSize}
              align={cardsAlign}
              height={!hasImage ? "100%" : "auto"}
              {...infoCard}
              infoCardsTitleSize={infoCardsTitleSize}
              infoCardsTitleColor={infoCardsTitleColor}
              card={
                card || {
                  useCard: true,
                  bg: cardsBackground,
                  borderRadius: "md",
                  boxShadow: cardsShadow,
                  theme: cardsTheme,
                }
              }
            />
          ))}
        </FilteredGrid>
      </Flex>
      {button && (
        <Flex mt={8} flexDirection="column" alignItems="center">
          <Button aria-label="CTA button 1" {...button} />
        </Flex>
      )}
    </Section>
  )
}

export default InfoCards

InfoCards.propTypes = {
  strapi_id: PropTypes.number.isRequired,
  section: PropTypes.shape(Section.strapiProps).isRequired,
  sectionTitle: PropTypes.shape(SectionTitle.strapiProps).isRequired,
  flexGrid: PropTypes.shape(FlexGrid.strapiProps),
  infoCards: PropTypes.arrayOf(PropTypes.shape(InfoCard.strapiProps)),
  cardsAlign: PropTypes.oneOf(["left", "center"]).isRequired,
  iconLocation: PropTypes.oneOf(["top", "left"]).isRequired,
  card: PropTypes.shape(Card.strapiProps).isRequired,
  image: PropTypes.object,
  imageLocation: PropTypes.oneOf(["left", "right"]),
  infoCardsTitleSize: PropTypes.oneOf(["h2", "h3"]),
}

export const query = graphql`
  fragment InfoCards on STRAPI__COMPONENT_SECTIONS_INFO_CARDS {
    strapi_id
    INFOCARDSSection: section {
      ...Section
    }
    sectionTitle {
      ...SectionTitle
    }
    InfoCFlexGrid: flexGrid {
      ...FlexGrid
    }
    infoCards {
      ...InfoCard
    }
    button {
      ...Button
    }
    card {
      ...Card
    }
    InfoCFilters: filters {
      ...Filter
    }
    InfoCFilter: filter {
      alignMenuItems
      filterLocation
      showFilterLabel
      layout
      filterTextDisplay
      dropdownCard {
        ...Card
      }
    }
    infoCardsTitleSize
    infoCardsTitleColor
    cardsAlign
    iconLocation
    iconSize
    cardsTheme
    cardsBackground
    cardsShadow
    image {
      ...ImageWithConstrainedLayout
    }
    useCarouselFront
    carousel {
      ...GenericCarousel
    }
    infoCardsImageLocation: imageLocation
  }
`
